<template>
  <div id="app">
    <BaseLayout class="font-open-sans">
      <section class="bg-white">
        <div class="mx-auto w-desktop space-y-8 pb-6 pt-4">
          <TheHeader @onOpenCashbackInfoModal="handleOpenCashbackInfoModal" />
          <div class="space-y-8">
            <h1 class="text-2xl font-bold">
              {{ $t('main-page.title') }}
            </h1>
            <SearchForm class="mt-4" @onSubmit="onSearchFormSubmit" />
          </div>
        </div>
      </section>
      <section class="mx-auto w-desktop space-y-16">
        <div class="mt-8 flex w-desktop gap-4">
          <ClientOnly>
            <PromoSection
              @onOpenRequestFormModal="handleOpenFeedbackRequestModal"
              @onOpenCashbackInfoModal="handleOpenCashbackInfoModal"
            />
          </ClientOnly>
        </div>
        <AdvantagesText class="mt-16" />
      </section>
      <ClientOnly>
        <div class="fixed bottom-4 z-30 w-full">
          <MaintenanceNotifications />
        </div>
        <CashbackInfoModal
          :name="CASHBACK_INFO_MODAL_NAME"
          @onOpenRequestFormModal="handleOpenFeedbackRequestModal"
        />
        <RequestFormModal
          :name="FEEDBACK_REQUEST_MODAL_NAME"
          :source="RequestSource.Main"
        />
      </ClientOnly>
    </BaseLayout>
  </div>
</template>

<script setup lang="ts">
  import { onMounted } from 'vue';

  import { NavigationLib } from '@shared/lib';
  import { ClientOnly } from '@shared/ui';
  import { Analytics } from '@shared/analytics';
  import { BaseLayout } from '@shared/ui/base-layout/desktop';
  import { TheHeader } from '@entities/the-header';
  import { SearchForm } from '@widgets/search-form/index.desktop';
  import { MaintenanceNotifications } from '@features/maintenance-notifications';
  import { injectAppContext } from '@shared/app-context';
  import type { SearchParams } from '@entities/search-params';

  import { CashbackInfoModal } from '@entities/cashback-info-modal';
  import { injectModal } from '@shared/modal';
  import { RequestSource, useOpenRequest } from '@widgets/request-form';
  import { RequestFormModal } from '@widgets/request-form/ui/desktop';
  import { analyticsEvents } from '../shared';

  import { AdvantagesText } from './ui';
  import PromoSection from './ui/PromoSection.vue';

  const appContext = injectAppContext();
  const modal = injectModal();
  const { openRequest } = useOpenRequest();

  const CASHBACK_INFO_MODAL_NAME = 'CASHBACK_INFO_MODAL_NAME';
  const FEEDBACK_REQUEST_MODAL_NAME = 'FEEDBACK_REQUEST_MODAL_NAME';

  function onSearchFormSubmit(searchParams: SearchParams) {
    Analytics.logEvent({
      event: analyticsEvents.SEARCH_FORM.SUBMIT,
    });

    window.open(
      NavigationLib.buildURL({
        locale: appContext.locale,
        path: `/tourx/search`,
        searchParams: new URLSearchParams(searchParams.buildQueryParams()),
      }),
      '_self',
    );
  }

  const handleOpenFeedbackRequestModal = () => {
    openRequest(() => modal.show(FEEDBACK_REQUEST_MODAL_NAME));

    Analytics.logEvent({
      event: analyticsEvents.REQUEST_CLICK,
    });
  };

  const handleOpenCashbackInfoModal = () => {
    modal.show(CASHBACK_INFO_MODAL_NAME);
  };

  onMounted(() => {
    Analytics.logEvent({
      event: analyticsEvents.OPEN,
    });
  });
</script>
